import compact from 'lodash/compact';

import { MappingSetState, MappingState, PackCalculatorState } from '../components/shared/types';
import { EVEN_PACKS } from '../reducers/editConstants';

// Validation of a single pack calculator
const validatePackCalculator = (packCalculator: PackCalculatorState) => {
  const hasName = Boolean(packCalculator.name);
  const hasPreferredContainerType = Boolean(packCalculator.preferredContainerType);
  const isUsingEvenSplitStrategy = packCalculator.shreddingStrategy === EVEN_PACKS;
  const packableQuantities =
    packCalculator.packableQuantities
      ?.split(',')
      .filter((value: string) => value !== '')
      .map(Number) ?? [];

  const preferredPackQuantity = Number(packCalculator.preferredPackQuantity);

  const packableQuantitiesValid =
    packableQuantities.length !== 0 && packableQuantities.every((value: number) => value > 0);

  const preferredPackQuantityValid =
    !packCalculator.preferredPackQuantity || new Set(packableQuantities).has(preferredPackQuantity);

  const maxQuantityValid = Boolean(packCalculator.maxQuantity);

  const hasValidQuantities = isUsingEvenSplitStrategy ? maxQuantityValid : packableQuantitiesValid;

  return hasName && hasPreferredContainerType && preferredPackQuantityValid && hasValidQuantities;
};

export const validateMappingSet = ({
  mappingSet,
  accountId,
}: {
  mappingSet: MappingSetState;
  accountId: string | undefined;
}) => {
  const hasName = Boolean(mappingSet.name.trim());
  const hasAccount = Boolean(accountId);
  const hasMappings = mappingSet.mappings.length !== 0;
  const mappingsAreValid = mappingSet.mappings.every((mapping: MappingState, index: number) => {
    const { packCalculator, packCalculatorList } = mapping;

    // Validate main pack calculator
    let mainPackCalculatorValid = true;
    if (!packCalculatorList) {
      mainPackCalculatorValid = validatePackCalculator(packCalculator);
    }

    // Validate pack calculator list
    let packCalculatorListValid = true;
    if (Array.isArray(packCalculatorList)) {
      if (packCalculatorList.length === 0) {
        packCalculatorListValid = false;
      } else {
        packCalculatorListValid = packCalculatorList.every((calculator, calcIndex) => {
          const isValid = validatePackCalculator(calculator);
          return isValid;
        });
      }
    }

    // Mapping-level validations (SKUs/attribute model)
    const hasAttributeModelUrl = Boolean(mapping.attributeModelUrl);
    const hasSkus = compact(mapping.skus).length > 0;
    const attributeModelFetchSuccessful = hasAttributeModelUrl && Boolean(mapping.attributeModel);

    // Must have either attribute model or SKUs
    const hasValidMappingData = hasAttributeModelUrl || hasSkus;
    const attributeModelValid = !hasAttributeModelUrl || attributeModelFetchSuccessful;

    const mappingValid =
      mainPackCalculatorValid && packCalculatorListValid && hasValidMappingData && attributeModelValid;

    return mappingValid;
  });

  const finalValid = hasName && hasAccount && hasMappings && mappingsAreValid;

  return finalValid;
};
